import { createAction } from '@reduxjs/toolkit'
import cloneDeep from 'lodash/cloneDeep'
import { all, call, put, select, takeEvery } from 'redux-saga/effects'

import { DRAWABLE_TYPES } from '../../../shared/constants/drawable-types'
import managers from '../../lib/managers'
import PaperManager from '../../lib/managers/PaperManager'
import { Select, Workspace } from '../../lib/toolBoxes/2D'
import { selectDrawableLocations } from '../../slices/2D'
import {
    selectMaterialModificationConflicts,
    updateMaterialModificationConflictToSave,
} from '../../slices/masterSetPlan'
import { IMUP2DDrawableLocation } from '../../types'

export const deleteModificationAction = createAction<number[]>('deleteModification')

export function* handleDeleteModification(action: ReturnType<typeof deleteModificationAction>) {
    const manager: PaperManager | null = yield call(managers.get2DManager)

    if (!manager) return

    const [selectTool, workspaceTool]: [Select, Workspace] = yield call(manager.getTools, [Select.NAME, Workspace.NAME])

    const { materialModificationConflicts }: ReturnType<typeof selectMaterialModificationConflicts> = yield select(
        selectMaterialModificationConflicts
    )

    const drawableLocations: IMUP2DDrawableLocation[] = yield select(selectDrawableLocations)

    const materialModificationConflictsIdsSet = new Set(action.payload.map((id) => id))

    const selectedModificationLocationIds = materialModificationConflicts.reduce((ids, m) => {
        if (materialModificationConflictsIdsSet.has(m.id)) {
            return [...ids, m.material_location_id]
        }

        return ids
    }, [] as number[])

    const materialConflictsList = materialModificationConflicts.filter((m) => {
        const location = drawableLocations.find((loc) => loc.opening_location_id === m.material_location_id)
        const isModInSelectedSet = materialModificationConflictsIdsSet.has(m.id)

        if (location?.drawing_type === DRAWABLE_TYPES.WALL) {
            // select all locations associated with the wall
            const wallOpeningLocations = drawableLocations.filter((loc) => loc.drawable_id === location.drawable_id)

            // if only one is found fall to return statement outside of if
            if (wallOpeningLocations.length !== 1) {
                const areAnyLocationsSelected = wallOpeningLocations.some((loc) =>
                    selectedModificationLocationIds.includes(loc.opening_location_id)
                )
                const selectedLocation = wallOpeningLocations.filter((loc) =>
                    selectedModificationLocationIds.includes(loc.opening_location_id)
                )

                // only delete the locations if the selected
                // location is the wall itself and not a king stud
                if (selectedLocation[0].coordinates.length > 1) {
                    return areAnyLocationsSelected
                }
            }
        }

        return isModInSelectedSet
    })

    // update coordinates to original state
    const materialConflictsIoDelete = materialConflictsList.map((m) => {
        const assignedLocation: IMUP2DDrawableLocation | undefined = drawableLocations.find(
            (loc) => loc.opening_location_id === m.material_location_id
        )
        const updatedMaterialConflict = cloneDeep(m)

        if (assignedLocation) {
            updatedMaterialConflict.coordinates = assignedLocation.coordinates
            updatedMaterialConflict.cutouts = assignedLocation.cutouts
        }

        return updatedMaterialConflict
    })

    yield put(updateMaterialModificationConflictToSave(materialConflictsIoDelete))

    // exit selected mode, remove handlers
    yield call(selectTool.exitSelectMode)

    const itemsToDelete: paper.Item[] | null = yield call(
        workspaceTool.getItemsWithCriteria,
        'data',
        (data) => !!materialConflictsIoDelete.find((mod) => data?.materialModificationConflict?.id === mod.id)
    )

    if (!itemsToDelete) return

    // remove items from blueprint
    yield all(itemsToDelete.map((item) => call([item, 'remove'])))
}

export function* watchForDeleteModification() {
    yield takeEvery(deleteModificationAction.type, handleDeleteModification)
}
